import { BlogPost } from '@/types/blog'
import { Button } from '@/components/ui/button'
import {
  CheckCircle2,
  MessageCircle,
  Heart,
  Brain,
  Users,
  Smile,
} from 'lucide-react'

const whatsappUrl =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20artigo%20sobre%20os%2012%20benef%C3%ADcios%20do%20futebol%20e%20gostaria%20de%20agendar%20uma%20aula%20para%20meu%20filho%21'

export const postBeneficios: BlogPost = {
  slug: 'beneficios-futebol-desenvolvimento-infantil',
  title:
    '12 Benefícios do Futebol para o Desenvolvimento Infantil (Comprovados)',
  excerpt:
    'Muito além do gol: conheça os 12 benefícios comprovados que o futebol traz para a saúde física, mental e social do seu filho.',
  date: '16 Fev, 2026',
  author: 'Equipe Pedagógica SuperFut',
  category: 'Saúde & Bem-estar',
  location: 'São Paulo, SP',
  image:
    'https://img.usecurling.com/p/800/400?q=kids%20soccer%20celebration%20happy',
  metaDescription:
    'Conheça os benefícios comprovados do futebol para crianças: coordenação, cognitivo, social. Estudos científicos.',
  faq: [
    {
      question: 'O futebol melhora o desempenho escolar?',
      answer:
        'Estudos indicam que sim. A atividade física regular melhora a oxigenação cerebral, foco e memória, impactando positivamente nos estudos.',
    },
    {
      question: 'Ajuda na disciplina em casa?',
      answer:
        'O esporte ensina o respeito às regras e à autoridade do treinador, o que frequentemente se reflete em melhor comportamento em casa.',
    },
    {
      question: 'Quantas vezes por semana é indicado?',
      answer:
        'A OMS recomenda 60 minutos de atividade diária. Aulas de futebol 2 a 3 vezes por semana são uma excelente base.',
    },
    {
      question: 'Futebol ajuda a dormir melhor?',
      answer:
        'Sim! O gasto energético saudável regula os hormônios do sono, proporcionando um descanso mais profundo e reparador.',
    },
    {
      question: 'Crianças asmáticas podem jogar?',
      answer:
        'Geralmente sim, e é benéfico para fortalecer a capacidade pulmonar, mas sempre consulte o pediatra antes de iniciar.',
    },
  ],
  content: (
    <div className="space-y-10 text-slate-700 leading-relaxed">
      <div className="prose prose-slate max-w-none">
        <p className="text-xl leading-relaxed">
          Você sabia que o futebol é considerado uma das atividades mais
          completas para o desenvolvimento infantil? Mais do que apenas um
          esporte popular, ele é uma ferramenta poderosa de transformação
          física, cognitiva e social. Apoiados em diretrizes da{' '}
          <strong>Organização Mundial da Saúde (OMS)</strong> e da{' '}
          <strong>Academia Americana de Pediatria</strong>, listamos 12
          benefícios cientificamente comprovados que o futebol pode trazer para
          a vida do seu filho.
        </p>
      </div>

      <div className="grid gap-8">
        {/* 1. Coordenação Motora */}
        <section>
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Brain className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-super-text">
              1. Coordenação Motora Global
            </h2>
          </div>
          <p>
            O futebol exige movimentos complexos: correr, saltar, girar e
            chutar, muitas vezes ao mesmo tempo. Isso desenvolve a coordenação
            motora grossa e fina, o equilíbrio e a noção espacial de forma
            superior a atividades mais estáticas.
          </p>
        </section>

        {/* 2. Desenvolvimento Cognitivo */}
        <section>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            2. Desenvolvimento Cognitivo Acelerado
          </h2>
          <p>
            O jogo exige tomada de decisão rápida. "Devo passar ou chutar?",
            "Para onde meu colega está correndo?". Esse processamento constante
            estimula o córtex pré-frontal, responsável pelo planejamento e
            resolução de problemas.
          </p>
        </section>

        {/* 3. Habilidades Sociais */}
        <section>
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-super-text">
              3. Socialização e Comunicação
            </h2>
          </div>
          <p>
            No futebol, a criança aprende a se comunicar, negociar espaço e
            interagir com pares. É um ambiente controlado e seguro para
            desenvolver a extroversão e fazer novos amigos fora do ambiente
            escolar.
          </p>
        </section>

        {/* 4. Autoestima */}
        <section>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            4. Fortalecimento da Autoestima
          </h2>
          <p>
            Cada gol, cada passe certo e cada evolução técnica é uma pequena
            vitória. Esse feedback positivo constante constrói a autoimagem da
            criança como alguém capaz e competente.
          </p>
        </section>

        {/* 5. Disciplina */}
        <section>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            5. Disciplina e Respeito às Regras
          </h2>
          <p>
            O futebol tem regras claras. Aprender a respeitá-las, assim como
            respeitar o árbitro (professor) e os adversários, cria bases sólidas
            de cidadania e comportamento.
          </p>
        </section>

        {/* 6. Trabalho em Equipe */}
        <section>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            6. Trabalho em Equipe
          </h2>
          <p>
            "Ninguém ganha sozinho". Essa é a lição máxima do esporte coletivo.
            A criança aprende a confiar nos outros, a dividir responsabilidades
            e a celebrar conquistas coletivas.
          </p>
        </section>

        {/* 7. Resiliência */}
        <section>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            7. Resiliência e Lidar com Frustrações
          </h2>
          <p>
            Perder faz parte. O futebol ensina a criança a lidar com a derrota,
            a levantar após uma queda e a tentar de novo, desenvolvendo
            inteligência emocional para a vida adulta.
          </p>
        </section>

        {/* 8. Saúde Cardiorrespiratória */}
        <section>
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-red-100 p-2 rounded-lg">
              <Heart className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-super-text">
              8. Saúde do Coração e Pulmões
            </h2>
          </div>
          <p>
            Sendo um exercício aeróbico, o futebol fortalece o coração, melhora
            a circulação e aumenta a capacidade pulmonar, prevenindo doenças
            como obesidade infantil e diabetes.
          </p>
        </section>

        {/* 9. Qualidade do Sono */}
        <section>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            9. Melhora na Qualidade do Sono
          </h2>
          <p>
            Criança que gasta energia dorme melhor. A atividade física regular
            regula o ciclo circadiano e promove um sono mais profundo e
            reparador, essencial para o crescimento.
          </p>
        </section>

        {/* 10. Redução de Telas */}
        <section>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            10. Menos Telas, Mais Vida Real
          </h2>
          <p>
            Cada hora jogando futebol é uma hora a menos no tablet ou celular. O
            esporte reconecta a criança com o mundo real, a natureza e as
            interações humanas presenciais.
          </p>
        </section>

        {/* 11. Vínculo Familiar */}
        <section>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            11. Fortalecimento do Vínculo Familiar
          </h2>
          <p>
            Quando os pais assistem, incentivam ou jogam junto (como nas nossas
            aulas a domicílio), cria-se uma memória afetiva poderosa que une a
            família.
          </p>
        </section>

        {/* 12. Rotina Saudável */}
        <section>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            12. Criação de Hábitos Saudáveis
          </h2>
          <p>
            Crianças ativas tendem a se tornar adultos ativos. Introduzir o
            esporte cedo cria o hábito da atividade física como parte natural da
            higiene de vida, assim como escovar os dentes.
          </p>
        </section>
      </div>

      <section className="bg-slate-100 p-8 rounded-3xl mt-12 text-center border-2 border-slate-200">
        <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
          Proporcione esses benefícios hoje mesmo!
        </h2>
        <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">
          Não espere mais para investir no futuro do seu filho. Com o SuperFut
          Kids, levamos toda essa estrutura de desenvolvimento para dentro do
          seu condomínio.
        </p>
        <Button
          size="lg"
          className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto"
          asChild
        >
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-6 h-6 mr-2" />
            Quero Agendar Agora
          </a>
        </Button>
      </section>
    </div>
  ),
}
